/**
 * toolMeta — Tool name to icon/color/category mapping for tool call UX.
 */

import type { ToolCategory } from '../types';

type ToolMeta = {
  icon: string;
  color: string;
  category: ToolCategory;
};

const MCP_PREFIX = 'mcp__jam-sql__';

/** Strip MCP prefix to get the short tool name. */
export function stripMcpPrefix(name: string): string {
  return name.startsWith(MCP_PREFIX) ? name.slice(MCP_PREFIX.length) : name;
}

const TOOL_REGISTRY: Record<string, ToolMeta> = {
  Read: { icon: '\u{1F4D6}', color: 'text-green-600 bg-green-50 dark:bg-green-950/30', category: 'file' },
  Write: { icon: '\u{270F}\u{FE0F}', color: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30', category: 'file' },
  Edit: { icon: '\u{1F527}', color: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30', category: 'file' },
  Bash: { icon: '\u{26A1}', color: 'text-purple-600 bg-purple-50 dark:bg-purple-950/30', category: 'system' },
  Glob: { icon: '\u{1F50D}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'search' },
  Grep: { icon: '\u{1F50E}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'search' },
  query_execute: { icon: '\u{1F4BE}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'database' },
  schema_list_tables: { icon: '\u{1F4CB}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'database' },
  ui_set_editor_text: { icon: '\u{270F}\u{FE0F}', color: 'text-green-600 bg-green-50 dark:bg-green-950/30', category: 'ui' },
  // PL/SQL source tools (read/blue)
  plsql_get_source: { icon: '\u{1F4C4}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  plsql_get_members: { icon: '\u{1F4CB}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  // PL/SQL compile tools (write/orange)
  plsql_compile: { icon: '\u{1F527}', color: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30', category: 'debug' },
  plsql_compile_all: { icon: '\u{1F527}', color: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30', category: 'debug' },
  plsql_compile_debug: { icon: '\u{1F527}', color: 'text-orange-600 bg-orange-50 dark:bg-orange-950/30', category: 'debug' },
  // Debug session (dangerous/red)
  debug_start: { icon: '\u{25B6}\u{FE0F}', color: 'text-red-600 bg-red-50 dark:bg-red-950/30', category: 'debug' },
  debug_stop: { icon: '\u{23F9}\u{FE0F}', color: 'text-red-600 bg-red-50 dark:bg-red-950/30', category: 'debug' },
  // Debug inspection (read/blue)
  debug_get_variables: { icon: '\u{1F50D}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  debug_get_call_stack: { icon: '\u{1F4DA}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  debug_evaluate_expression: { icon: '\u{1F9EE}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  debug_get_breakpoints: { icon: '\u{1F534}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  debug_get_source: { icon: '\u{1F4C4}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  debug_get_messages: { icon: '\u{1F4AC}', color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/30', category: 'debug' },
  // Debug execution (write/amber)
  debug_step_over: { icon: '\u{23ED}\u{FE0F}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  debug_step_into: { icon: '\u{2B07}\u{FE0F}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  debug_step_out: { icon: '\u{2B06}\u{FE0F}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  debug_continue: { icon: '\u{23E9}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  // Debug breakpoint management (write/amber)
  debug_set_breakpoint: { icon: '\u{1F534}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  debug_remove_breakpoint: { icon: '\u{26AA}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  debug_enable_breakpoint: { icon: '\u{1F7E2}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
  debug_disable_breakpoint: { icon: '\u{1F7E1}', color: 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30', category: 'debug' },
};

const NOTEBOOK_PREFIX_META: ToolMeta = {
  icon: '\u{1F4D3}',
  color: 'text-teal-600 bg-teal-50 dark:bg-teal-950/30',
  category: 'notebook',
};

const DEFAULT_META: ToolMeta = {
  icon: '\u{1F528}',
  color: 'text-muted-foreground bg-muted/50',
  category: 'other',
};

export function getToolMeta(rawName: string): ToolMeta {
  const name = stripMcpPrefix(rawName);
  if (TOOL_REGISTRY[name]) return TOOL_REGISTRY[name];
  if (name.startsWith('notebook_')) return NOTEBOOK_PREFIX_META;
  if (name.startsWith('schema_')) return TOOL_REGISTRY.schema_list_tables;
  if (name.startsWith('query_')) return TOOL_REGISTRY.query_execute;
  if (name.startsWith('table_')) return TOOL_REGISTRY.query_execute;
  if (name.startsWith('ui_')) return TOOL_REGISTRY.ui_set_editor_text;
  if (name.startsWith('connections_')) return TOOL_REGISTRY.query_execute;
  if (name.startsWith('transaction_')) return TOOL_REGISTRY.query_execute;
  if (name.startsWith('data_compare_')) return TOOL_REGISTRY.schema_list_tables;
  if (name.startsWith('schema_compare_')) return TOOL_REGISTRY.schema_list_tables;
  if (name.startsWith('plsql_')) return TOOL_REGISTRY.plsql_get_source;
  if (name.startsWith('debug_')) return TOOL_REGISTRY.debug_get_variables;
  return DEFAULT_META;
}

/** Human-readable labels for MCP tool short names. */
const HUMAN_TOOL_NAMES: Record<string, string> = {
  // Connections
  connections_list: 'List Connections',
  connections_connect: 'Connect to Database',
  // Query execution
  query_execute: 'Execute Query',
  // UI
  ui_open_tab: 'Open Tab',
  ui_focus_tab: 'Focus Tab',
  ui_set_editor_text: 'Set Editor Text',
  ui_execute_query: 'Run Query in Editor',
  ui_close_tab: 'Close Tab',
  ui_lock_tab: 'Lock Tab',
  ui_unlock_tab: 'Unlock Tab',
  ui_check_abort: 'Check Abort',
  ui_list_tabs: 'List Open Tabs',
  ui_screenshot: 'Take Screenshot',
  ui_export_results: 'Export Results to File',
  // Table explorer
  table_set_filter: 'Set Table Filter',
  table_set_sort: 'Sort Table',
  table_set_pagination: 'Set Pagination',
  table_get_snapshot: 'Get Table Snapshot',
  table_refresh: 'Refresh Table',
  table_select_row: 'Select Row',
  table_show_row_details: 'Show Row Details',
  table_show_chart: 'Show Chart',
  table_set_chart_config: 'Configure Chart',
  // Query results
  query_select_row: 'Select Result Row',
  query_show_row_details: 'Show Row Details',
  query_switch_tab: 'Switch Result Tab',
  query_set_result_display: 'Set Result Display',
  query_execute_with_plan: 'Execute with Plan',
  query_get_execution_plan: 'Get Execution Plan',
  query_get_messages: 'Get Query Messages',
  query_get_results: 'Get Query Results',
  query_set_chart_config: 'Configure Results Chart',
  query_show_chart: 'Show Results Chart',
  // Schema
  schema_list_tables: 'List Tables',
  schema_list_views: 'List Views',
  schema_list_procedures: 'List Procedures',
  schema_list_functions: 'List Functions',
  schema_get_columns: 'Get Columns',
  schema_get_indexes: 'Get Indexes',
  schema_get_constraints: 'Get Constraints',
  schema_get_dependencies: 'Get Dependencies',
  // Schema compare
  schema_compare_start: 'Start Schema Compare',
  schema_compare_get_result: 'Get Compare Result',
  schema_compare_generate_script: 'Generate Compare Script',
  schema_compare_cancel: 'Cancel Schema Compare',
  // Data compare
  data_compare_start: 'Start Data Compare',
  data_compare_get_result: 'Get Compare Result',
  data_compare_get_diff_rows: 'Get Diff Rows',
  data_compare_generate_script: 'Generate Sync Script',
  data_compare_cancel: 'Cancel Data Compare',
  // Notebook
  notebook_list_cells: 'List Cells',
  notebook_get_cell: 'Get Cell',
  notebook_add_cell: 'Add Cell',
  notebook_set_cell_source: 'Edit Cell',
  notebook_delete_cell: 'Delete Cell',
  notebook_move_cell: 'Move Cell',
  notebook_convert_cell: 'Convert Cell',
  notebook_run_cell: 'Run Cell',
  notebook_run_all: 'Run All Cells',
  notebook_stop_execution: 'Stop Execution',
  notebook_clear_outputs: 'Clear Outputs',
  notebook_restart_session: 'Restart Session',
  notebook_save: 'Save Notebook',
  // Transactions
  transaction_set_mode: 'Set Transaction Mode',
  transaction_begin: 'Begin Transaction',
  transaction_commit: 'Commit Transaction',
  transaction_rollback: 'Rollback Transaction',
  // PL/SQL
  plsql_get_source: 'Get Source',
  plsql_get_members: 'Get Members',
  plsql_compile: 'Compile',
  plsql_compile_all: 'Compile All',
  plsql_compile_debug: 'Compile Debug',
  // Debug session
  debug_start: 'Start Debug',
  debug_stop: 'Stop Debug',
  // Debug inspection
  debug_get_variables: 'Get Variables',
  debug_get_call_stack: 'Get Call Stack',
  debug_evaluate_expression: 'Evaluate Expression',
  debug_get_breakpoints: 'Get Breakpoints',
  debug_get_source: 'Get Program Source',
  debug_get_messages: 'Get Messages',
  // Debug execution
  debug_step_over: 'Step Over',
  debug_step_into: 'Step Into',
  debug_step_out: 'Step Out',
  debug_continue: 'Continue',
  // Debug breakpoints
  debug_set_breakpoint: 'Set Breakpoint',
  debug_remove_breakpoint: 'Remove Breakpoint',
  debug_enable_breakpoint: 'Enable Breakpoint',
  debug_disable_breakpoint: 'Disable Breakpoint',
};

/** Get a human-readable tool name. Returns short name for MCP tools, raw name for others. */
export function getHumanToolName(rawName: string): string {
  const short = stripMcpPrefix(rawName);
  return HUMAN_TOOL_NAMES[short] ?? short;
}

/** Check if a tool is a Jam SQL MCP tool (with or without the mcp__ prefix). */
export function isMcpTool(rawName: string): boolean {
  if (rawName.startsWith(MCP_PREFIX)) return true;
  // Codex sends bare tool names without the mcp__jam-sql__ prefix
  return rawName in HUMAN_TOOL_NAMES;
}

/** Extract a short inline summary from tool input for compact display. */
export function getToolInlineSummary(rawName: string, input: Record<string, unknown>): string {
  const toolName = stripMcpPrefix(rawName);

  if (toolName === 'Read' && input.file_path) return String(input.file_path);
  if (toolName === 'Write' && input.file_path) return String(input.file_path);
  if (toolName === 'Edit' && input.file_path) return String(input.file_path);
  if (toolName === 'Bash' && input.command) return String(input.command).slice(0, 60);
  if (toolName === 'query_execute' && input.sql) return String(input.sql).slice(0, 60);
  if (toolName === 'ui_set_editor_text') return 'updating editor';

  // Schema tools — show table/view/object name
  if (toolName === 'schema_get_columns' && (input.objectName || input.table)) return String(input.objectName ?? input.table);
  if (toolName === 'schema_get_indexes' && input.tableName) return String(input.tableName);
  if (toolName === 'schema_get_constraints' && input.tableName) return String(input.tableName);
  if (toolName === 'schema_get_dependencies' && (input.objectName || input.object)) return String(input.objectName ?? input.object);
  if (toolName.startsWith('schema_') && (input.objectName || input.table)) return String(input.objectName ?? input.table);

  // Notebook tools — show cell id, type, or position
  if (toolName === 'notebook_run_cell' && input.cellId) return `run cell ${input.cellId}`;
  if (toolName === 'notebook_add_cell' && input.cellType) return `add ${input.cellType} cell`;
  if (toolName === 'notebook_set_cell_source' && input.cellId) return `edit cell ${input.cellId}`;
  if (toolName === 'notebook_delete_cell' && input.cellId) return `delete cell ${input.cellId}`;
  if (toolName === 'notebook_move_cell' && input.cellId) return `move cell ${input.cellId}`;
  if (toolName === 'notebook_convert_cell' && input.cellId) return `convert cell ${input.cellId}`;
  if (toolName === 'notebook_get_cell' && input.cellId) return `cell ${input.cellId}`;
  if (toolName.startsWith('notebook_') && input.cellId) return `cell ${input.cellId}`;

  // PL/SQL tools — show object name
  if (toolName.startsWith('plsql_') && input.objectName) return String(input.objectName);

  // Debug tools — show expression or breakpoint info
  if (toolName === 'debug_evaluate_expression' && input.expression) return String(input.expression).slice(0, 60);
  if (toolName === 'debug_set_breakpoint' && input.line) return `line ${input.line}`;
  if (toolName === 'debug_remove_breakpoint' && input.breakpointId) return `bp ${input.breakpointId}`;
  if (toolName === 'debug_enable_breakpoint' && input.breakpointId) return `bp ${input.breakpointId}`;
  if (toolName === 'debug_disable_breakpoint' && input.breakpointId) return `bp ${input.breakpointId}`;

  return '';
}
